import { useEffect, useMemo, useState } from "react";
import {
  api,
  type AdminAlertSettings,
  type AdminAlertSettingsPatch,
} from "../../lib/api";
import {
  categoriesChanged,
  categoryLabel,
  changedCategories,
} from "./alertCategories";

const WEBHOOK_PREFIX = "https://hooks.slack.com/";

function relativeTime(iso: string | null): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const seconds = (Date.now() - t) / 1000;
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

/**
 * Admin → Alerts. Configure the Slack Incoming Webhook that receives
 * operational alerts (one toggle per category the backend reports). The
 * webhook URL is write-only: the server returns a masked hint, and a blank URL
 * field on save keeps the existing webhook. Changes propagate within ~30s
 * (server-side cache TTL).
 */
export function AlertsPage() {
  const [doc, setDoc] = useState<AdminAlertSettings | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [webhookInput, setWebhookInput] = useState("");
  const [channelLabel, setChannelLabel] = useState("");
  const [categories, setCategories] = useState<Record<string, boolean>>({});
  const [cooldown, setCooldown] = useState(300);
  const [error, setError] = useState<string | null>(null);
  const [testMsg, setTestMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState<"" | "load" | "save" | "test">("load");

  /** Load the server's settings into the form; the webhook field always starts blank. */
  function hydrate(d: AdminAlertSettings) {
    setEnabled(d.enabled);
    setChannelLabel(d.channel_label);
    setCategories({ ...d.categories });
    setCooldown(d.dedup_cooldown_seconds);
    setWebhookInput("");
  }

  useEffect(() => {
    setBusy("load");
    api.admin.alerts
      .get()
      .then((d) => {
        setDoc(d);
        hydrate(d);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setBusy(""));
  }, []);

  const dirty = useMemo(() => {
    if (!doc) return false;
    return (
      enabled !== doc.enabled ||
      channelLabel !== doc.channel_label ||
      categoriesChanged(doc.categories, categories) ||
      cooldown !== doc.dedup_cooldown_seconds ||
      webhookInput.trim() !== ""
    );
  }, [doc, enabled, channelLabel, categories, cooldown, webhookInput]);

  /** PUT the form (only the category toggles that changed), then re-hydrate. */
  async function save() {
    const wh = webhookInput.trim();
    if (wh && !wh.startsWith(WEBHOOK_PREFIX)) {
      setError(`Webhook URL must start with ${WEBHOOK_PREFIX} (a Slack Incoming Webhook).`);
      return;
    }
    setBusy("save");
    setError(null);
    setTestMsg(null);
    try {
      const patch: AdminAlertSettingsPatch = {
        enabled,
        channel_label: channelLabel,
        // Categories go as a diff (the backend merges per key), so a stale page
        // cannot revert another admin's toggle on a category it never touched.
        // The other fields are still sent whole, as before.
        categories: changedCategories(doc?.categories ?? {}, categories),
        dedup_cooldown_seconds: cooldown,
      };
      if (wh) patch.slack_webhook_url = wh;
      const updated = await api.admin.alerts.put(patch);
      setDoc(updated);
      hydrate(updated);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy("");
    }
  }

  async function sendTest() {
    setBusy("test");
    setTestMsg(null);
    setError(null);
    try {
      const r = await api.admin.alerts.test();
      setTestMsg(
        r.ok
          ? { ok: true, text: "Test message sent — check the channel." }
          : { ok: false, text: r.error ?? "Failed to send test message." },
      );
    } catch (e) {
      setTestMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusy("");
    }
  }

  if (busy === "load" && !doc) return <p className="text-slate-500">Loading…</p>;

  return (
    <div className="max-w-2xl">
      <h1 className="mb-2 text-2xl font-semibold">Alerts</h1>
      <p className="mb-1 text-sm text-slate-500">
        Send Slack alerts when the platform refuses an action: blocked{" "}
        <strong>BigQuery</strong> queries (allowlist denials, write attempts,
        byte cap, rate limits) and blocked <strong>Jira</strong> queries
        (allowlist denials, rejected JQL, rate limits, response cap) plus Jira
        scope violations. A category added after setup starts off until you
        enable it here. Scope violations also page through Cloud Monitoring
        regardless of this toggle.
      </p>
      <p className="mb-4 text-xs text-slate-400">
        Last updated {relativeTime(doc?.updated_at ?? null)}
        {doc?.updated_by ? ` by ${doc.updated_by}` : ""}. Changes propagate within ~30s.
      </p>

      {error && (
        <p className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </p>
      )}

      <div className="space-y-4">
        {/* Enable toggle */}
        <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-4">
          <div>
            <h2 className="text-sm font-semibold">Alerting enabled</h2>
            <p className="text-xs text-slate-500">
              Master switch. When off, nothing is sent (the test button still works).
            </p>
          </div>
          <label className="inline-flex cursor-pointer items-center">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
          </label>
        </div>

        {/* Slack webhook */}
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h2 className="mb-1 text-sm font-semibold">Slack Incoming Webhook</h2>
          <p className="mb-3 text-xs text-slate-500">
            Paste a webhook from{" "}
            <code>api.slack.com → Your App → Incoming Webhooks</code>. Must start with{" "}
            <code>{WEBHOOK_PREFIX}</code>.{" "}
            {doc?.webhook_configured ? (
              <span className="text-emerald-700">
                Configured ({doc.webhook_hint}). Leave blank to keep it.
              </span>
            ) : (
              <span className="text-amber-700">Not configured yet.</span>
            )}
          </p>
          <input
            type="password"
            autoComplete="off"
            placeholder={`${WEBHOOK_PREFIX}services/…`}
            value={webhookInput}
            onChange={(e) => setWebhookInput(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-1.5 font-mono text-sm"
          />
          <div className="mt-2 flex items-center gap-3">
            <label className="text-xs text-slate-600">
              Channel label{" "}
              <input
                type="text"
                placeholder="#infosec-alerts"
                value={channelLabel}
                onChange={(e) => setChannelLabel(e.target.value)}
                className="ml-1 rounded-md border border-slate-300 px-2 py-1 text-sm"
              />
            </label>
            <span className="text-xs text-slate-400">(display only)</span>
          </div>
          <div className="mt-3">
            <button
              type="button"
              onClick={sendTest}
              disabled={!doc?.webhook_configured || busy === "test"}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
            >
              {busy === "test" ? "Sending…" : "Send test message"}
            </button>
            {webhookInput.trim() && (
              <span className="ml-2 text-xs text-slate-400">
                Save first to test a newly entered URL.
              </span>
            )}
            {testMsg && (
              <span
                className={`ml-2 text-xs ${testMsg.ok ? "text-emerald-700" : "text-rose-700"}`}
              >
                {testMsg.text}
              </span>
            )}
          </div>
        </div>

        {/* Categories + dedup */}
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h2 className="mb-2 text-sm font-semibold">What to alert on</h2>
          {Object.keys(categories)
            .sort()
            .map((key) => (
              <label key={key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={categories[key] ?? false}
                  onChange={(e) =>
                    setCategories((prev) => ({ ...prev, [key]: e.target.checked }))
                  }
                />
                {categoryLabel(key)}
              </label>
            ))}
          <div className="mt-4 flex items-center gap-2">
            <label className="text-sm">
              Dedup cooldown (seconds)
              <input
                type="number"
                min={0}
                max={86400}
                value={cooldown}
                onChange={(e) => setCooldown(Math.max(0, Number(e.target.value) || 0))}
                className="ml-2 w-24 rounded-md border border-slate-300 px-2 py-1 text-sm"
              />
            </label>
            <span className="text-xs text-slate-400">
              Repeats of the same block are suppressed for this long. 0 disables dedup.
            </span>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            disabled={!dirty || busy === "save"}
            onClick={() => {
              if (doc) hydrate(doc);
              setError(null);
              setTestMsg(null);
            }}
            className="rounded-md border border-slate-300 px-4 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!dirty || busy === "save"}
            className="rounded-md bg-slate-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
          >
            {busy === "save" ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
